<template>
  <div><Highcharts :options="chartOptions"></Highcharts></div>
</template>

<script>
  import { Chart } from "highcharts-vue";
  import Highcharts from "highcharts";
  // Since Highcharts 12, modules register themselves on import
  import "highcharts/modules/exporting";
  import { getAcronymAbbrHtml } from "@/utils/acronyms";
  import { getFlowAxisRange } from "@/utils/flowAxis";
  import { getCotationForFlow, sortFlowLevels } from "@/utils/flowLevels";
  import { getZoneColors, ZONE_BAND_COLORS } from "@/utils/zoneColors";

  Highcharts.setOptions({
    lang: {
      shortMonths: ["janv", "févr", "mars", "avril", "mai", "juin", "juill", "août", "sept", "oct", "nov", "déc"],
      weekdays: ["Dimanche", "Lundi", "Mardi", "Mercredi", "Jeudi", "Vendredi", "Samedi"],
    },
  });

  export default {
    components: {
      Highcharts: Chart,
    },
    props: {
      minSugLevel: {
        type: Number,
      },
      maxSugLevel: {
        type: Number,
      },
      recommendedSugLevel: {
        type: [Number, String],
      },
      showSweetSpotBounds: {
        type: Boolean,
        default: false,
      },
      minCotation: {
        type: String,
        default: "",
      },
      baseCotation: {
        type: String,
        default: "",
      },
      maxCotation: {
        type: String,
        default: "",
      },
      levelSectionHistory: {
        type: Array,
      },
      // Section flow_levels; when set, each one is drawn as a line and gives the tooltip its cotation
      flowLevels: {
        type: Array,
        default: () => [],
      },
    },
    data() {
      return {
        chartOptions: {
          chart: {
            type: "spline",
            spacingLeft: 8,
            spacingRight: 8,
            spacingTop: 8,
            spacingBottom: 8,
          },
          title: {
            text: null,
          },
          legend: {
            enabled: false,
          },
          credits: {
            enabled: false,
          },
          xAxis: {
            type: "datetime",
            title: {
              text: null,
            },
            dateTimeLabelFormats: {
              minute: "%H:%M",
              hour: "%H:%M",
              day: "%e. %b",
              month: "%b '%y",
            },
            plotBands: [
              {
                from: "",
                to: "",
                color: "rgba(68, 170, 213, .2)",
              },
            ],
            crosshair: true,
          },
          tooltip: {
            valueSuffix: "",
          },
          yAxis: {
            softMax: this.maxSugLevel,
            softMin: this.minSugLevel,
            title: {
              text: "Débit",
            },
            plotBands: [
              {
                label: "Niveaux bas",
                from: this.minSugLevel,
                to: 0,
                color: "#faa8a2",
              },
              {
                label: "Niveaux recommandés",
                from: this.minSugLevel,
                to: this.maxSugLevel,
                color: "#8cdb8f",
              },
              {
                label: "Niveaux haut",
                from: this.maxSugLevel,
                to: 999999999,
                color: "#b0d8f7",
              },
            ],
          },
          series: [
            {
              name: "Débit",
              color: "#1565C0",
              data: [], // sample data
            },
          ],
          exporting: {
            enabled: false,
          },
          responsive: {
            rules: [
              {
                condition: {
                  maxWidth: 600,
                },
                chartOptions: {
                  chart: {
                    spacingLeft: 2,
                    spacingRight: 2,
                    spacingTop: 4,
                    spacingBottom: 4,
                  },
                  xAxis: {
                    title: {
                      text: null,
                    },
                    labels: {
                      style: {
                        fontSize: "10px",
                      },
                    },
                  },
                  yAxis: [
                    {
                      title: {
                        text: null,
                      },
                      labels: {
                        style: {
                          fontSize: "10px",
                        },
                      },
                    },
                    {
                      labels: {
                        style: {
                          fontSize: "10px",
                        },
                      },
                    },
                  ],
                },
              },
            ],
          },
        },
      };
    },
    computed: {
      // Axis, grid and tooltip colours of the dark theme are in app.scss
      isDark() {
        return Boolean(this.$vuetify.theme.current && this.$vuetify.theme.current.dark);
      },
    },
    methods: {
      getNiceRoundedValue(value, step) {
        if (!Number.isFinite(value)) {
          return 0;
        }
        if (!Number.isFinite(step) || step <= 0) {
          return Math.round(value);
        }
        return Math.round(value / step) * step;
      },
      getNiceTickStep(range, targetTickCount) {
        if (!Number.isFinite(range) || range <= 0) {
          return 1;
        }
        const rawStep = range / targetTickCount;
        const magnitude = 10 ** Math.floor(Math.log10(rawStep));
        const normalized = rawStep / magnitude;
        // Closest of 1, 2, 2.5, 5 and 10
        const niceNormalized = [1, 2, 2.5, 5, 10].reduce((best, value) =>
          Math.abs(value - normalized) < Math.abs(best - normalized) ? value : best,
        );
        return niceNormalized * magnitude;
      },
      getRoundingStep(min, max) {
        const spread = Math.abs(max - min);

        if (spread >= 20) {
          return 5;
        }
        if (spread >= 10) {
          return 2;
        }
        return 1;
      },

      /**
       * Creates a standardized flow scale by dividing the min-max range into 5 equal steps.
       * This is used to calculate the "sweet spot" (optimal paddling conditions).
       *
       * SWEET SPOT FORMULA:
       * - Divide range into 5 equal intervals: step = (max - min) / 5
       * - L1 (index 0) = min (0% into range)
       * - L2 (index 1) = min + step (20% into range) ← Sweet Spot START
       * - L3 (index 2) = min + 2×step (40% into range) ← Sweet Spot END (optimal level)
       * - L4 (index 3) = min + 3×step (60% into range)
       * - L5 (index 4) = max (100% into range)
       *
       * Example: min=100, max=500 → step=80
       *   L1=100, L2=180 (green line min), L3=260 (green line max/base), L4=340, L5=500
       *   Sweet spot zone: 180-260 mcs
       *
       * @param {number} min - Minimum suggested level (min_sug_level)
       * @param {number} max - Maximum suggested level (max_sug_level)
       * @returns {Array} Array of 5 scale points with values, labels, and flow indicators
       */
      getStandardizedScale(min, max) {
        if (!Number.isFinite(min) || !Number.isFinite(max) || max <= min) {
          return [];
        }

        const step = (max - min) / 5;

        if (!Number.isFinite(step) || step <= 0) {
          return [];
        }

        const valueStep = this.getRoundingStep(min, max);
        // L2: 20% into range (sweet spot start)
        const l2 = this.getNiceRoundedValue(min + step, valueStep);
        // L3: 40% into range (sweet spot end, the base/optimal level)
        const l3 = this.getNiceRoundedValue(min + 2 * step, valueStep);
        // L4: 60% into range
        const l4 = this.getNiceRoundedValue(min + 3 * step, valueStep);

        const minLabel = this.minCotation || this.baseCotation || this.maxCotation || "";
        const baseLabel = this.baseCotation || this.minCotation || this.maxCotation || "";
        const maxLabel = this.maxCotation || this.baseCotation || this.minCotation || "";

        // 5 ticks alternating: cotation → flow → cotation → flow → cotation
        return [
          { value: min, label: minLabel, isFlow: false },
          { value: l2, label: ``, isFlow: true },
          { value: l3, label: baseLabel, isFlow: false },
          { value: l4, label: ``, isFlow: true },
          { value: max, label: maxLabel, isFlow: false },
        ];
      },
      getData() {
        const isMobile = Boolean(this.$vuetify && this.$vuetify.display && this.$vuetify.display.smAndDown);
        const colors = getZoneColors(this.isDark);

        let chartData = [];
        this.levelSectionHistory.forEach((dataHistory) => {
          chartData.unshift([
            Date.parse(dataHistory.date.replace(/-/g, "/") + " " + dataHistory.hour + " GMT"),
            Number(dataHistory.flow),
          ]);
        });

        const min = Number(this.minSugLevel);
        const max = Number(this.maxSugLevel);
        const cotationScale = this.getStandardizedScale(min, max);
        const minLabel = this.minCotation || this.baseCotation || this.maxCotation || "-";
        const baseLabel = this.baseCotation || this.minCotation || this.maxCotation || "-";
        const maxLabel = this.maxCotation || this.baseCotation || this.minCotation || "-";
        const hasRecommendedLevel =
          this.recommendedSugLevel !== null &&
          this.recommendedSugLevel !== undefined &&
          this.recommendedSugLevel !== "";
        const recommendedLevel = hasRecommendedLevel ? Number(this.recommendedSugLevel) : NaN;
        const flowLevels = sortFlowLevels(this.flowLevels);
        const hasFlowLevels = flowLevels.length > 0;

        /**
         * SWEET SPOT EXTRACTION AND VISUALIZATION
         *
         * From the standardized 5-point scale, extract the optimal flow zone:
         * - sweetSpotStart = cotationScale[1].value = min + step (L2, 20% into range)
         * - sweetSpotEnd = cotationScale[2].value = min + 2×step (L3, 40% into range)
         *
         * These can be rendered as two dashed green lines on the chart with labels
         * when showSweetSpotBounds is enabled.
         *
         * The zone between these lines represents the best paddling conditions.
         */
        const sweetSpotStart = cotationScale.length ? cotationScale[1].value : min;
        const sweetSpotEnd = cotationScale.length ? cotationScale[2].value : max;
        const upperShiftLevel = cotationScale.length ? cotationScale[3].value : max;
        const sweetSpotLines =
          this.showSweetSpotBounds &&
          Number.isFinite(sweetSpotStart) &&
          Number.isFinite(sweetSpotEnd) &&
          sweetSpotEnd > sweetSpotStart
            ? [
                {
                  value: sweetSpotStart,
                  color: "#2e7d32",
                  width: 2,
                  dashStyle: "ShortDash",
                  zIndex: 5,
                  label: {
                    text: "Conditions optimales min",
                    align: "right",
                    x: -6,
                    style: {
                      color: "#2e7d32",
                    },
                  },
                },
                {
                  value: sweetSpotEnd,
                  color: "#1b5e20",
                  width: 2,
                  dashStyle: "ShortDash",
                  zIndex: 5,
                  label: {
                    text: "Conditions optimales max",
                    align: "right",
                    x: -6,
                    style: {
                      color: "#1b5e20",
                    },
                  },
                },
              ]
            : [];

        const flowValues = chartData.map(([, flow]) => flow);
        const hasRange = Number.isFinite(min) && Number.isFinite(max) && max > min;

        const zoneBoundaryLines = [
          {
            value: min,
            color: colors.low,
            width: 2,
            dashStyle: "ShortDash",
            zIndex: 4,
            label: {
              text: isMobile ? "min" : "minimum recommande",
              align: "left",
              x: isMobile ? 2 : 6,
              style: { color: colors.low, fontWeight: "bold", fontSize: isMobile ? "9px" : "11px" },
            },
          },
          {
            value: max,
            color: colors.high,
            width: 2,
            dashStyle: "ShortDash",
            zIndex: 4,
            label: {
              text: isMobile ? "max" : "maximum recommande",
              align: "left",
              x: isMobile ? 2 : 6,
              style: { color: colors.high, fontWeight: "bold", fontSize: isMobile ? "9px" : "11px" },
            },
          },
        ];

        // Kept for when the recommended-level line is shown again (visibleRecommendedLine below)
        // eslint-disable-next-line no-unused-vars
        const recommendedLine = Number.isFinite(recommendedLevel)
          ? [
              {
                value: recommendedLevel,
                color: "#1b5e20",
                width: 2,
                zIndex: 6,
                label: {
                  text: "niveau recommandé",
                  align: "left",
                  x: 6,
                  style: { color: "#1b5e20", fontWeight: "bold", fontSize: "11px" },
                },
              },
            ]
          : [];

        const visibleRecommendedLine = [];

        // One dotted line per flow level, coloured by its zone; its label is on the right axis
        const getZoneColor = (flow) => {
          if (!Number.isFinite(min) || !Number.isFinite(max)) return colors.none;
          if (flow < min) return colors.low;
          if (flow > max) return colors.high;
          return colors.recommended;
        };
        const zoneBandColors = ZONE_BAND_COLORS;
        const flowLevelLines = flowLevels.map((level) => ({
          value: level.flow,
          color: getZoneColor(level.flow),
          width: 1,
          dashStyle: "Dot",
          zIndex: 5,
        }));
        const flowLevelValues = [...new Set(flowLevels.map((level) => level.flow))];

        // Same range as the sparklines of the river lists (utils/flowAxis.js)
        const { min: axisMin, max: axisMax } = getFlowAxisRange(flowValues, { min, max, levelFlows: flowLevelValues });

        this.chartOptions.tooltip = {
          formatter() {
            const flow = Number(this.y);
            let cotation = minLabel;

            if (hasFlowLevels) {
              cotation = getCotationForFlow(flowLevels, flow) || "-";
            } else if (Number.isFinite(flow) && Number.isFinite(upperShiftLevel) && flow >= upperShiftLevel) {
              cotation = maxLabel;
            } else if (Number.isFinite(flow) && Number.isFinite(sweetSpotStart) && flow >= sweetSpotStart) {
              cotation = baseLabel;
            }

            return `
              <b>${Highcharts.dateFormat("%e %b %H:%M", this.x)}</b><br/>
              Débit: <b>${flow} ${getAcronymAbbrHtml("mcs")}</b><br/>
              Cotation calculée: <b>${cotation}</b>
            `;
          },
        };

        // Evenly spaced mcs ticks
        const tickStep = this.getNiceTickStep(axisMax - axisMin, isMobile ? 4 : 6);
        const mcsTicks = [];
        for (let tick = Math.ceil(axisMin / tickStep) * tickStep; tick <= axisMax; tick += tickStep) {
          mcsTicks.push(Math.round(tick * 1000) / 1000);
        }

        // With flow levels, both axes tick at the flow levels: the flow on the left lines up with its
        // gauge mark and cotation on the right. Otherwise the left axis is evenly spaced and the right
        // axis shows the 3-point cotation scale on its own.
        const leftTickPositions = hasFlowLevels ? flowLevelValues : mcsTicks;
        const rightTickPositions = hasFlowLevels
          ? flowLevelValues
          : cotationScale.filter((scale) => !scale.isFlow).map((scale) => scale.value);

        this.chartOptions.yAxis = [
          {
            min: axisMin,
            max: axisMax,
            startOnTick: false,
            endOnTick: false,
            tickPositions: leftTickPositions,
            labels: {
              align: "right",
              x: isMobile ? -2 : 0,
              formatter() {
                return `${Math.round(this.value * 100) / 100} mcs`;
              },
              style: {
                fontSize: isMobile ? "9px" : "11px",
              },
            },
            title: {
              text: "",
            },
            plotBands: [
              {
                label: "Niveaux bas",
                from: axisMin,
                to: min,
                color: zoneBandColors.low,
              },
              {
                label: "Niveaux recommandés",
                from: min,
                to: max,
                color: zoneBandColors.recommended,
              },
              {
                label: "Niveaux haut",
                from: max,
                to: axisMax,
                color: zoneBandColors.high,
              },
            ],
            plotLines: [...zoneBoundaryLines, ...sweetSpotLines, ...visibleRecommendedLine, ...flowLevelLines],
          },
          {
            opposite: true,
            title: {
              text: null,
            },
            min: axisMin,
            max: axisMax,
            startOnTick: false,
            endOnTick: false,
            tickPositions: rightTickPositions,
            labels: {
              useHTML: true,
              reserveSpace: !isMobile,
              align: isMobile ? "right" : "left",
              x: isMobile ? -8 : 0,
              formatter() {
                const fontSize = isMobile ? "10px" : "11px";
                if (hasFlowLevels) {
                  const text = flowLevels
                    .filter((level) => Math.abs(level.flow - this.value) < 0.0001)
                    .map((level) => [level.gauge_mark, level.cotation].filter(Boolean).join(" · "))
                    .filter(Boolean)
                    .join(" / ");
                  return `<span style="color:${getZoneColor(this.value)};font-weight:700;font-size:${fontSize};">${text}</span>`;
                }
                const point = cotationScale.find((scale) => Math.abs(scale.value - this.value) < 0.0001);
                if (!point) {
                  return "";
                }
                if (point.isFlow) {
                  return "";
                }
                // color matches the corresponding plot band zone
                const color = point.value === min ? colors.low : point.value === max ? colors.high : colors.recommended;
                return `<span style="color:${color};font-weight:700;font-size:${fontSize};">${point.label}</span>`;
              },
            },
            gridLineWidth: 0,
          },
        ];

        var today = new Date();
        var todayFromLimit = Date.parse(
          today.getFullYear() +
            "-" +
            (today.getMonth() + 1 < 10 ? "0" : "") +
            (today.getMonth() + 1) +
            "-" +
            (today.getDate() + 1 < 10 ? "0" : "") +
            today.getDate() +
            " 00:00:00 GMT",
        );
        this.chartOptions.xAxis.from = todayFromLimit;

        var todayToLimit = Date.parse(
          today.getFullYear() +
            "-" +
            (today.getMonth() + 1 < 10 ? "0" : "") +
            (today.getMonth() + 1) +
            "-" +
            (today.getDate() + 1 < 10 ? "0" : "") +
            today.getDate() +
            " 23:59:59 GMT",
        );
        this.chartOptions.xAxis.to = todayToLimit;

        // The latest reading gets a marker and a label so the current flow reads at a glance
        const seriesData = chartData.map(([x, y], index) => {
          if (index !== chartData.length - 1) {
            return [x, y];
          }
          const color = getZoneColor(y);
          return {
            x,
            y,
            marker: { enabled: true, radius: 6, fillColor: color, lineColor: colors.outline, lineWidth: 2 },
            dataLabels: {
              enabled: true,
              useHTML: true,
              align: "right",
              x: -10,
              y: -12,
              formatter() {
                const time = Highcharts.dateFormat("%e %b %H:%M", this.x);
                return `<span class="current-flow-label" style="border-color:${color};"><b style="color:${color};">${this.y} mcs</b> · ${time}</span>`;
              },
            },
          };
        });

        this.chartOptions.series = [
          {
            name: "Débit",
            type: "areaspline",
            color: colors.high,
            lineWidth: 3,
            threshold: null,
            fillColor: {
              linearGradient: { x1: 0, y1: 0, x2: 0, y2: 1 },
              stops: [
                [0, `rgba(${colors.area}, 0.18)`],
                [1, `rgba(${colors.area}, 0)`],
              ],
            },
            marker: { enabled: false },
            // Line colour follows the zone it crosses: red below the range, green inside, blue above
            zones: hasRange
              ? [{ value: min, color: colors.low }, { value: max + 0.0001, color: colors.recommended }, { color: colors.high }]
              : [],
            data: seriesData,
          },
        ];

        // "Now": a gold dashed line through the latest reading, the same role as the gold mark of the logo
        const latest = chartData[chartData.length - 1];
        this.chartOptions.xAxis.plotLines = latest
          ? [{ value: latest[0], color: colors.now, width: 2, dashStyle: "ShortDash", zIndex: 3 }]
          : [];
      },
    },
    created() {
      this.getData();
    },
    watch: {
      isDark() {
        this.getData();
      },
      levelSectionHistory: {
        deep: true,
        handler() {
          this.getData();
        },
      },
      minSugLevel() {
        this.getData();
      },
      maxSugLevel() {
        this.getData();
      },
      recommendedSugLevel() {
        this.getData();
      },
      minCotation() {
        this.getData();
      },
      baseCotation() {
        this.getData();
      },
      maxCotation() {
        this.getData();
      },
      flowLevels: {
        deep: true,
        handler() {
          this.getData();
        },
      },
    },
  };
</script>

<style lang="scss" scoped>
  // Highcharts renders the HTML data label outside the scoped tree
  :deep(.current-flow-label) {
    display: inline-block;
    padding: 2px 8px;
    border: 1px solid;
    border-radius: 12px;
    background: var(--qww-chart-label-bg);
    font-size: 12px;
    font-weight: 400;
    color: var(--qww-chart-label-color);
    white-space: nowrap;
  }
</style>
